import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getDefaultStore } from 'jotai';
import { currentUserAtom, authLoadedAtom } from '../auth/AuthAtoms';
import { completeSocialSignup, fetchUserDetails } from '../api/auth';
import axios from '../api/axios';
import { toast } from 'react-toastify';

const store = getDefaultStore();

const ChooseUsernamePage = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        const appleToken = sessionStorage.getItem('apple_identity_token');
        if (!token && !appleToken) {
            navigate('/login');
        }
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmed = username.trim();
        if (!trimmed) {
            toast.error('Username is required');
            return;
        }

        setLoading(true);
        const provider = sessionStorage.getItem('social_provider') || 'google';

        try {
            if (provider === 'apple') {
                // Apple: use native /auth/social/complete with identity token
                const identityToken = sessionStorage.getItem('apple_identity_token');
                const nonce = sessionStorage.getItem('apple_nonce');
                if (!identityToken) throw new Error('Missing Apple token — please sign in again');

                const response = await axios.post('/auth/social/complete', {
                    provider: 'apple',
                    identityToken,
                    nonce,
                    username: trimmed,
                });

                const { AccessToken, RefreshToken, user } = response.data;
                localStorage.setItem('accessToken', AccessToken);
                if (RefreshToken) localStorage.setItem('refreshToken', RefreshToken);
                localStorage.setItem('authOrigin', 'apple');
                store.set(currentUserAtom, user);
                store.set(authLoadedAtom, true);
                localStorage.setItem('username', user.username);
                sessionStorage.removeItem('apple_identity_token');
                sessionStorage.removeItem('apple_nonce');
                sessionStorage.removeItem('social_provider');
                toast.success('Account created successfully');
                navigate('/');
            } else {
                // Google: use Cognito web flow
                await completeSocialSignup('google', trimmed);
                const userDetails = await fetchUserDetails(localStorage.getItem('accessToken'));

                if (userDetails?.user) {
                    store.set(currentUserAtom, userDetails.user);
                    store.set(authLoadedAtom, true);
                    localStorage.setItem('username', userDetails.user.username);
                    localStorage.setItem('authOrigin', 'cognito-oauth');
                    toast.success('Account created successfully');
                    navigate('/');
                } else {
                    toast.error('Account created but failed to load user data');
                    navigate('/login');
                }
            }
        } catch (err) {
            sessionStorage.removeItem('apple_identity_token');
            sessionStorage.removeItem('apple_nonce');
            sessionStorage.removeItem('social_provider');
            toast.error(err.message || 'Failed to complete signup');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <h2>Choose a Username</h2>
            <p>One last step — pick a username for your account.</p>
            <form onSubmit={handleSubmit}>
                <label>
                    Username:
                    <input
                        type="text"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        placeholder="Enter a username"
                        required
                        autoFocus
                    />
                </label>
                <button type="submit" disabled={loading}>
                    {loading ? 'Creating account...' : 'Continue'}
                </button>
            </form>
            <p className="tc-consent">
                By continuing, you agree to our <Link to="/terms">Terms</Link>.
            </p>
        </div>
    );
};

export default ChooseUsernamePage;
